// Centralized Authentication Context - Production Ready
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase, getSecureSession } from './secureSupabase'
import { updateLastLogin } from '../utils/updateLastLogin'
import {
  isSandboxMode,
  sandboxAuthBypass,
  getSandboxSession,
  sandboxSignOut
} from './sandboxAuth'

const AuthContext = createContext()

// Debug logging only in development
const debugLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args)
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [initialized, setInitialized] = useState(false)
  const [authDebugInfo] = useState({
    deviceInfo: {
      userAgent: navigator.userAgent,
      isMobile: /Mobi|Android/i.test(navigator.userAgent),
      platform: navigator.platform,
      cookiesEnabled: navigator.cookieEnabled
    }
  })

  // Initialize authentication with URL session handling and timeout
  const initializeAuth = useCallback(async () => {
    const authTimeout = setTimeout(() => {
      console.warn('⏰ Authentication initialization timeout - forcing completion')
      setLoading(false)
      setInitialized(true)
      setUser(null)
    }, 15000) // 15 second timeout to prevent infinite loading

    try {
      // SANDBOX MODE: Bypass authentication for testing
      if (isSandboxMode()) {
        debugLog('🏖️ SANDBOX MODE DETECTED - Using auth bypass')

        try {
          // Check for existing sandbox session first
          let sandboxData = getSandboxSession()

          if (!sandboxData) {
            // No existing session, create one via bypass
            debugLog('🔓 Creating sandbox session...')
            sandboxData = await sandboxAuthBypass()
          }

          if (sandboxData?.user) {
            debugLog('✅ Sandbox auth successful:', sandboxData.user.email)
            setUser(sandboxData.user)
          }

          clearTimeout(authTimeout)
          setLoading(false)
          setInitialized(true)
          return
        } catch (error) {
          console.error('❌ Sandbox auth failed:', error)
          setUser(null)
          clearTimeout(authTimeout)
          setLoading(false)
          setInitialized(true)
          return
        }
      }

      debugLog('🔐 Initializing secure authentication...')

      // STREAMLINED: Use Supabase's native authentication detection
      // Supabase automatically handles URL fragments like: #access_token=xyz&refresh_token=abc
      // This is secure, scalable, and maintained by Supabase
      const hashParams = new URLSearchParams(window.location.hash.substring(1))
      const accessToken = hashParams.get('access_token')
      const refreshToken = hashParams.get('refresh_token')

      if (accessToken && refreshToken) {
        debugLog('🔗 Found session tokens in URL fragment, setting session...')
        try {
          // Create session object from URL fragment
          const sessionFromUrl = {
            access_token: accessToken,
            refresh_token: refreshToken,
            token_type: hashParams.get('token_type') || 'bearer',
            expires_in: parseInt(hashParams.get('expires_in') || '3600'),
            expires_at: Math.floor(Date.now() / 1000) + parseInt(hashParams.get('expires_in') || '3600')
          }

          // Set the session in Supabase with timeout
          const sessionController = new AbortController()
          const sessionTimeout = setTimeout(() => sessionController.abort(), 10000) // 10 second timeout

          const { data, error } = await Promise.race([
            supabase.auth.setSession(sessionFromUrl),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Session setup timeout')), 10000)
            )
          ])

          clearTimeout(sessionTimeout)

          if (error) throw error

          if (data.user) {
            debugLog('✅ Session set from URL fragment:', data.user.email)
            setUser(data.user)
          }

          // Clean up URL fragment for security
          window.history.replaceState({}, '', window.location.pathname)

          // Clear auth timeout and early return
          clearTimeout(authTimeout)
          setLoading(false)
          setInitialized(true)
          return
        } catch (error) {
          console.error('❌ Failed to set session from URL:', error)
        }
      }

      // Get session (either from URL processing or localStorage) with timeout
      const session = await Promise.race([
        getSecureSession(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Session retrieval timeout')), 8000)
        )
      ])

      if (session?.user) {
        debugLog('✅ User authenticated:', session.user.email)
        setUser(session.user)

        // Update last login timestamp (non-blocking)
        updateLastLogin(session.user.id).catch(err =>
          console.warn('Failed to update last login:', err)
        )
      } else {
        debugLog('❌ No authenticated user found')
        setUser(null)
      }
    } catch (error) {
      console.error('❌ Auth initialization error:', error)
      setUser(null)
    } finally {
      clearTimeout(authTimeout)
      setLoading(false)
      setInitialized(true)
    }
  }, [])

  // SIMPLIFIED: Basic PostMessage support for cross-domain communication
  // Only used as fallback when URL fragment authentication isn't available
  useEffect(() => {
    const handleCrossSubdomainAuth = async (event) => {
      // Security: Only allow our trusted domains
      const allowedOrigins = [
        'https://atsinsiderai.com',
        'https://www.atsinsiderai.com'
      ]

      if (!allowedOrigins.includes(event.origin)) {
        return // Silently ignore unauthorized origins
      }

      // Handle Supabase session from website
      if (event.data.type === 'SUPABASE_SESSION' && event.data.session) {
        debugLog('📨 Received Supabase session via PostMessage')

        try {
          // Use Supabase's native setSession method
          const { data, error } = await supabase.auth.setSession(event.data.session)

          if (error) throw error

          if (data.user) {
            debugLog('✅ PostMessage session established:', data.user.email)
            setUser(data.user)
            setLoading(false)
            setInitialized(true)

            // Update last login (non-blocking)
            updateLastLogin(data.user.id).catch(err =>
              console.warn('Failed to update last login:', err)
            )
          }
        } catch (error) {
          console.error('❌ PostMessage session failed:', error)
        }
      }
    }

    // Add PostMessage listener
    window.addEventListener('message', handleCrossSubdomainAuth)

    // Initialize auth on component mount
    initializeAuth()

    return () => {
      window.removeEventListener('message', handleCrossSubdomainAuth)
    }
  }, [initializeAuth])

  // Listen for authentication state changes
  useEffect(() => {
    if (!initialized) return

    debugLog('🔄 Setting up auth state listener')

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session, error) => {
        debugLog(`🔄 Auth state change: ${event}`, session?.user?.email)

        // Handle refresh token errors gracefully
        if (error) {
          if (error.message?.includes('Already Used') || error.message?.includes('Invalid Refresh Token')) {
            console.warn('🔄 Refresh token already used - clearing stale tokens and signing out')
            // Clear all Supabase tokens
            if (typeof window !== 'undefined') {
              Object.keys(localStorage).forEach(key => {
                if (key.startsWith('sb-') || key.includes('supabase')) {
                  localStorage.removeItem(key)
                }
              })
            }
            // Sign out silently
            await supabase.auth.signOut()
            setUser(null)
            setLoading(false)
            return
          }
          console.error('🔄 Auth state change error:', error)
          return
        }

        switch (event) {
          case 'SIGNED_IN':
            setUser(session?.user || null)
            setLoading(false)
            break

          case 'SIGNED_OUT':
            setUser(null)
            setLoading(false)
            break

          case 'TOKEN_REFRESHED':
            debugLog('🔄 Token refreshed successfully')
            setUser(session?.user || null)
            break

          case 'USER_UPDATED':
            setUser(session?.user || null)
            break

          default:
            debugLog('🔄 Other auth event:', event)
            break
        }
      }
    )

    return () => {
      subscription?.unsubscribe()
    }
  }, [initialized])

  // Secure sign out function
  const signOut = useCallback(async () => {
    try {
      setLoading(true)
      debugLog('🚪 Signing out securely...')

      if (isSandboxMode()) {
        sandboxSignOut()
        setUser(null)
        debugLog('✅ Sandbox sign out successful')
      } else {
        await supabase.auth.signOut()
        setUser(null)
        debugLog('✅ Signed out successfully')
      }
    } catch (error) {
      console.error('❌ Sign out error:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // Get user profile with additional security checks
  const getUserProfile = useCallback(async () => {
    if (!user) return null

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error fetching user profile:', error)
      return null
    }
  }, [user])

  // Debug helper function for mobile troubleshooting
  const getAuthDebugInfo = useCallback(() => {
    return {
      ...authDebugInfo,
      currentUrl: window.location.href,
      referrer: document.referrer,
      hasParentWindow: window.parent !== window,
      localStorage: {
        hasSupabaseToken: !!localStorage.getItem('supabase.auth.token'),
        cookiesEnabled: navigator.cookieEnabled
      },
      environment: {
        nodeEnv: process.env.NODE_ENV,
        hostname: window.location.hostname,
        protocol: window.location.protocol
      }
    }
  }, [authDebugInfo])

  // Auth context value
  const value = {
    // User state
    user,
    loading,
    initialized,

    // Computed properties
    isAuthenticated: !!user,
    userId: user?.id || null,
    userEmail: user?.email || null,

    // Functions
    signOut,
    getUserProfile,
    getAuthDebugInfo,

    // For debugging (remove in production)
    _debug: {
      supabaseClient: supabase,
      authDebugInfo
    }
  }

  // Show loading state while initializing
  if (loading && !initialized) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-white mb-2">Authenticating...</h2>
          <p className="text-gray-400">Verifying your session securely</p>
        </div>
      </div>
    )
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider